import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import config from '../../../../config.js';

interface LegalEntityDto {
  id: number;
  le_id: string;
  name: string;
  country: string;
  address: string;
  status_active: string;
  created_date: number;
  last_updated_date: number | null;
}

export interface BcrStatusDto {
  id: number;
  status_name: string;
  created_date: number;
  last_updated_date: number | null;
}

export interface LegalEntityDetailDto {
  legal_entity: LegalEntityDto;
  bcr_status: BcrStatusDto | null; // allow null if backend omits
}

export interface CountryGroup {
  country: string;
  legal_entities: LegalEntityDto[];
}

@Injectable({ providedIn: 'root' })
export class LegalEntityService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  // Fallback allows tests/local run if config not populated yet
  private readonly endpoint = `${this.base}/legal_entities`;

  constructor(private http: HttpClient) {}

  getLegalEntities(): Observable<CountryGroup[]> {
    return this.http.get<CountryGroup[]>(this.endpoint);
  }

  /**
   * Fetch the list of BCR status objects from the backend.
   */
  getBcrStatuses(): Observable<BcrStatusDto[]> {
    const base = this.base;
    const endpoint = `${base}/legal_entities/bcr_status`;
    return this.http.get<BcrStatusDto[]>(endpoint);
  }

  getLegalEntityDetail(entityId: number): Observable<LegalEntityDetailDto> {
    return this.http.get<LegalEntityDetailDto>(`${this.endpoint}/detail/${entityId}`);
  }

  /**
   * Check whether a user has permission for a given module/role.
   * Expects body: { prid_user, role_name: string[], module_name: string[] }
   */
  hasPermission(pridUser: string) {
    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: ['PST member'],
      module_name: ['Legal Entity List'],
    };
    return this.http.post<{ has_permission: boolean }>(endpoint, body);
  }

  /**
   * Request a change of BCR status for a legal entity.
   * Expects payload: { user_prid, legal_entity_id, bcr_status_id, link }
   */
  changeBcrStatus(userPrid: string, legalEntityId: number, bcrStatusId: number, link: string) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status_request`;
    const body = {
      user_prid: userPrid,
      legal_entity_id: legalEntityId,
      bcr_status_id: bcrStatusId,
      link,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }

  /**
   * Approve or reject a BCR status change request.
   * Expects payload with all notification data + request_status: 'Approved' | 'Rejected'
   */
  updateBcrStatusRequest(
    id: number,
    author: string,
    approver: string,
    createdDate: number,
    requestStatus: 'Approved' | 'Rejected',
    legalEntityId: number,
    newBcrStatusId: number,
    previousBcrStatusId: number,
    message: string = '',
    lastUpdatedDate: number | null = null,
  ) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status`;
    const body = {
      id,
      author,
      message,
      approver,
      created_date: createdDate,
      request_status: requestStatus,
      legal_entity_id: legalEntityId,
      last_updated_date: lastUpdatedDate,
      new_bcr_status_id: newBcrStatusId,
      previous_bcr_status_id: previousBcrStatusId,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }
}
